import { useMutation } from "@tanstack/react-query"
import { format } from "date-fns"

import supabase from "@/supabase"

interface OrientationBookingData {
  applicationId: string
  sessionId: string
  date: Date
}

interface SessionBooking {
  id: number
  session_id: string
  application_id: number
  attended: boolean
  date: string
  created_at?: string
}

interface ScheduleResponse {
  success: boolean
  message?: string
  data?: {
    booking: SessionBooking
    date: string
  }
}

/**
 * Hook for scheduling orientation sessions via cloud function
 */
export const useOrientationCloudFunctions = () => {
  // Schedule orientation session
  const scheduleOrientation = useMutation({
    mutationFn: async ({
      applicationId,
      sessionId,
      date,
    }: OrientationBookingData): Promise<ScheduleResponse> => {
      if (!applicationId || !sessionId || !date) {
        throw new Error("Application ID, Session ID, and Date are required")
      }

      try {
        // For timestamp (without timezone) in Postgres/Supabase, we need to format the date
        // as an ISO string but then remove the timezone part to preserve local time
        const localDateTimeString = format(date, "yyyy-MM-dd HH:mm:ss")

        const { data, error } = await supabase.functions.invoke("schedule-orientation", {
          body: {
            applicationId,
            sessionId,
            date: localDateTimeString,
          },
        })

        if (error) {
          console.error("Error scheduling orientation:", error)
          try {
            // Try to parse the error response body to get the actual error message
            const parsedError = await error.context.json()
            return parsedError
          } catch {
            // If parsing fails, fall back to error.message or default message
            return {
              success: false,
              message: error.message || "Failed to schedule orientation",
            }
          }
        }

        return data
      } catch (error) {
        console.error("Error in scheduleOrientation:", error)
        throw error
      }
    },
  })

  return {
    scheduleOrientation,
  }
}
